import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Calendar, Users, User } from 'lucide-react';
import { apiGet } from '../../api/client';
import { formatDate, formatEventType } from '../../utils/format';
import { EventStatusBadge } from '../ui/Badge';

interface SearchResult {
  events: Array<{ id: string; title: string; eventType: string; status: string; eventDate: string; client: { firstName: string; lastName: string } }>;
  clients: Array<{ id: string; firstName: string; lastName: string; email: string; phone?: string }>;
  collaborators: Array<{ id: string; name: string; email: string; role: string }>;
}

type FlatResult = { type: 'event' | 'client' | 'collaborator'; id: string; href: string; primary: string; secondary: string; extra?: React.ReactNode };

export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Open on Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ('');
      setActiveIdx(0);
    }
  }, [open]);

  const { data, isFetching } = useQuery<SearchResult>({
    queryKey: ['global-search', q],
    queryFn: () => apiGet<SearchResult>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });

  const flat: FlatResult[] = [
    ...(data?.events ?? []).map(e => ({
      type: 'event' as const,
      id: e.id,
      href: `/events/${e.id}`,
      primary: e.title,
      secondary: `${e.client.firstName} ${e.client.lastName} · ${formatDate(e.eventDate)} · ${formatEventType(e.eventType)}`,
      extra: <EventStatusBadge status={e.status as never} />,
    })),
    ...(data?.clients ?? []).map(c => ({
      type: 'client' as const,
      id: c.id,
      href: `/clients`,
      primary: `${c.firstName} ${c.lastName}`,
      secondary: c.email + (c.phone ? ` · ${c.phone}` : ''),
    })),
    ...(data?.collaborators ?? []).map(c => ({
      type: 'collaborator' as const,
      id: c.id,
      href: `/collaborators`,
      primary: c.name,
      secondary: `${c.role} · ${c.email}`,
    })),
  ];

  const go = useCallback((href: string) => {
    setOpen(false);
    navigate(href);
  }, [navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flat.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && flat[activeIdx]) go(flat[activeIdx].href);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flat, activeIdx, go]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  if (!open) return null;

  const typeIcon = (type: FlatResult['type']) => {
    if (type === 'event') return <Calendar size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />;
    if (type === 'client') return <User size={14} style={{ color: 'var(--color-warning)', flexShrink: 0 }} />;
    return <Users size={14} style={{ color: 'var(--color-gold)', flexShrink: 0 }} />;
  };

  const typeLabel = (type: FlatResult['type']) => ({ event: 'Evento', client: 'Cliente', collaborator: 'Collaboratore' }[type]);

  // Group label: show once per type
  const showLabel = (i: number) => i === 0 || flat[i].type !== flat[i - 1].type;

  const isEmpty = q.trim().length >= 2 && !isFetching && flat.length === 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))', WebkitBackdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))' }}
      onClick={() => setOpen(false)}
    >
      <div
        style={{ width: '100%', maxWidth: 580, background: 'var(--glass-bg-elevated)', backdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))', WebkitBackdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 'var(--radius-container-lg)', boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.06) inset', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Search size={17} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Cerca eventi, clienti, collaboratori…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--color-text)', fontSize: 'var(--text-md)' }}
          />
          {q && (
            <button onClick={() => setQ('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0, display: 'flex' }}>
              <X size={16} />
            </button>
          )}
          <kbd style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', background: 'var(--glass-bg-input)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', padding: '2px 8px', flexShrink: 0 }}>ESC</kbd>
        </div>

        {/* Results */}
        {q.trim().length < 2 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)' }}>
            Digita almeno 2 caratteri per cercare
          </div>
        ) : isFetching ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            Ricerca in corso…
          </div>
        ) : isEmpty ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)' }}>
            Nessun risultato per "<strong style={{ color: 'var(--color-text-muted)' }}>{q}</strong>"
          </div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {flat.map((item, i) => (
              <React.Fragment key={`${item.type}-${item.id}`}>
                {showLabel(i) && (
                  <div style={{ padding: '0.5rem 1rem 0.25rem', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(255,255,255,0.03)' }}>
                    {typeLabel(item.type)}
                  </div>
                )}
                <button
                  onClick={() => go(item.href)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    width: '100%', padding: '0.625rem 1rem', background: i === activeIdx ? 'rgba(255,255,255,0.06)' : 'none',
                    border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                    cursor: 'pointer', textAlign: 'left', transition: 'background var(--spring-duration) var(--spring-easing)',
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  {typeIcon(item.type)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.primary}</p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.secondary}</p>
                  </div>
                  {item.extra}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Footer hint */}
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.03)', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
          <span><kbd style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', padding: '1px 5px' }}>↑↓</kbd> naviga</span>
          <span><kbd style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', padding: '1px 5px' }}>↵</kbd> apri</span>
          <span><kbd style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', padding: '1px 5px' }}>ESC</kbd> chiudi</span>
        </div>
      </div>
    </div>
  );
}
